import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import StyledWrapper from '../../style';
import Table from '../../../../components/Table/Table';
import ProcessTemplateColumns from '../../components/processTemplateColumns/processTemplateColumns';
import ButtonDropdown from '../../../../components/core/ButtonDropdown/ButtonDropdown';
import DeleteDialog from '../../../../components/DeleteDialog/DeleteDialog';
import FormBuilderModal from '../../../FormBuilderModal/FormBuilderModal';
import StageTemplateCreation from '../../../StageTemplateCreationModal/StageTemplateCreation';
import { clearMetaInfo } from '../../../../components/FormBuilderLibrary/slice';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import usePaginatedCall from '../../../../hooks/usePaginatedCall';
import { useGetReviewFormTemplatesQuery } from '../../../../services/endpoints/formBuilderEndpoints';
import { getEntityPrefix } from '../../../../utils/utilFunctions';
import useActions from '../../hooks/useActions';

const ReviewTemplates = () => {
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [columnWidth, setColumnWidth] = useState({});
	const [alertBalloon, setAlertBalloon] = useState(false);

	const handleAlert = (message, status) => {
		setAlertBalloon({ isOpen: true, message, status });
		setTimeout(() => {
			setAlertBalloon(false);
		}, 3000);
	};
	const initialColumns = ProcessTemplateColumns(columnWidth, handleAlert);

	const [finalData, setFinalData] = useState([]);
	const [openFormModal, setOpenFormModal] = useState(false);
	const [openStageCreation, setOpenStageCreation] = useState(false);
	const [selectedRows, setSelectedRows] = useState([]);
	const [showValue, setShowValue] = useState('all');

	const tableRef = useRef(null);

	const pagination = usePaginatedCall(useGetReviewFormTemplatesQuery, {
		overallFilter: showValue,
		filterBaseName: 'REVIEW_TEMPLATES'
	});

	const handleChange = (event) => {
		setShowValue(event.target.value);
	};

	useEffect(() => {
		const isFormModal = searchParams.get('formBuilder');
		isFormModal && setOpenFormModal(isFormModal);

		const isStageCreation = searchParams.get('stageCreation');
		isStageCreation && setOpenStageCreation(isStageCreation);
	}, [searchParams]);

	const bulkActionButton = useActions('form', selectedRows, handleAlert, tableRef);

	useEffect(() => {
		const formattedData = pagination?.rows?.results?.map((form) => {
			const formType = form?.form_type === 'application' ? 'application-form' : 'review-form';
			const FormPrefix = getEntityPrefix({ type: formType, isBaseTemplate: true });

			return {
				hierarchy: [form.form_id],
				type: form.form_type === 'application' ? 'Application Form' : 'Review Form',
				table_id: `${FormPrefix}${form.unique_identifier}`,
				title: form.label,
				published: form.created_on,
				description: form.description,
				updated: form.last_updated_on,
				times_shared: form.times_shared,
				form_id: form.form_id,
				stage_id: form.stage?.stage_id,
				isDeletable: true,
				is_archived: form.is_archived,
				linked_count: form.linked_count
			};
		});

		setFinalData(formattedData || []);
	}, [pagination?.rows]);

	return (
		<>
			<StyledWrapper.FormHeader>
				<StyledWrapper.LastEditedActions>
					<>
						<StyledWrapper.ShowDropdownContainer>
							Show
							<FormControl sx={{ m: 1, minWidth: 120 }} size="small">
								<Select id="show-templates-dropdown" value={showValue} onChange={handleChange}>
									<MenuItem value={'all'}>All Templates</MenuItem>
									<MenuItem value={'active'}>Active Only</MenuItem>
									<MenuItem value={'archived'}>Archived Only</MenuItem>
								</Select>
							</FormControl>
						</StyledWrapper.ShowDropdownContainer>
						<StyledWrapper.ActionButtonContainer>
							<ButtonDropdown data={bulkActionButton} />
							<Button
								onClick={() => {
									setSearchParams({
										...searchParams,
										formBuilder: 'review'
									});
								}}
								size="small"
								data-testid={'templates-newTemplate-button'}
								variant="contained"
							>
								NEW REVIEW FORM TEMPLATE
							</Button>
						</StyledWrapper.ActionButtonContainer>
					</>
				</StyledWrapper.LastEditedActions>
			</StyledWrapper.FormHeader>
			<Collapse in={alertBalloon}>
				<Alert
					severity={alertBalloon?.status}
					action={
						<IconButton
							aria-label="close"
							color={alertBalloon?.status}
							size="small"
							onClick={() => {
								setAlertBalloon(false);
							}}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}
					sx={{ mb: 2, mt: 2 }}
				>
					{alertBalloon?.message}
				</Alert>
			</Collapse>
			<Table
				customHeight={'64vh'}
				columns={initialColumns}
				rowHeight={true}
				enableAutoPageSize
				rows={finalData || []}
				rowCount={pagination?.rowCount}
				pagination={pagination?.pagination}
				pageSize={pagination?.pageSize}
				paginationMode={pagination?.paginationMode}
				onPageChange={(e) => pagination?.onPageChange(e)}
				onPageSizeChange={(e) => pagination?.onPageSizeChange(e)}
				page={pagination?.page}
				loading={pagination?.loading}
				sortingMode={pagination?.sortingMode}
				onSortModelChange={(e) => pagination?.onSortModelChange(e)}
				onFilterModelChange={(e) => pagination?.onFilterModelChange(e)}
				onSelectChange={(e) => setSelectedRows(e)}
				disableSelectionOnClick
				key={'FormTemplates'}
				checkboxSelection
				onColumnResize={(e) => {
					const newColumnSizes = e?.colDef?.field;
					const width = e?.width;
					setColumnWidth({ ...columnWidth, [newColumnSizes]: width });
				}}
				getRowId={(row) => row.form_id}
				keepNonExistentRowsSelected
				hideDisabledCheckboxes
				filterModel={pagination?.filterModel}
				sortModel={pagination?.sortModel}
				excludeColumns={['action', 'baseForms', 'adHocForms', 'linked_count']}
				ref={tableRef}
			/>

			{openFormModal && (
				<FormBuilderModal
					isTemplate
					closeModal={() => {
						dispatch(clearMetaInfo());
						setOpenFormModal(false);
					}}
					totalForms={0}
				/>
			)}

			{openStageCreation && (
				<StageTemplateCreation isTemplate closeModal={() => setOpenStageCreation(false)} />
			)}

			<DeleteDialog
				refetch={pagination?.refreshCall}
				selectedRows={selectedRows}
				props={{ type: 'Review form', is_template: true }}
				handleAlert={handleAlert}
			/>
		</>
	);
};

export default ReviewTemplates;
